import { supabase } from './supabaseClient';

export class QRSoftDeleteService {
  /**
   * Soft delete a QR code (move to trash)
   */
  static async softDeleteQRCode(qrCodeId, userId) {
    try {
      console.log('🗑️ Calling soft_delete_qr_code RPC:', { qrCodeId, userId });
      
      const { data, error } = await supabase
        ?.rpc('soft_delete_qr_code', {
          qr_code_id: qrCodeId,
          deleted_by_user_id: userId
        });

      console.log('🗑️ RPC Response:', { data, error });

      if (error) {
        console.error('🗑️ RPC Error:', error);
        return { success: false, error: error.message };
      }

      // Check if the function returned false (user doesn't own QR or already deleted)
      if (data === false) {
        console.warn('🗑️ Function returned false - ownership check failed');
        return { success: false, error: 'QR code not found, already deleted, or you do not own it' };
      }

      return { success: data, error: null };
    } catch (error) {
      console.error('🗑️ Soft delete QR code error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Restore a QR code from trash
   */
  static async restoreQRCode(qrCodeId, userId) {
    try {
      const { data, error } = await supabase
        ?.rpc('restore_qr_code', {
          qr_code_id: qrCodeId,
          restored_by_user_id: userId
        });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: data, error: null };
    } catch (error) {
      console.error('Restore QR code error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Permanently delete a QR code (cannot be restored)
   */
  static async permanentlyDeleteQRCode(qrCodeId, userId) {
    try {
      console.log('🗑️ Calling permanently_delete_qr_code RPC:', { qrCodeId, userId });
      
      const { data, error } = await supabase
        ?.rpc('permanently_delete_qr_code', {
          qr_code_id: qrCodeId,
          deleted_by_user_id: userId
        });

      console.log('🗑️ Permanent delete RPC Response:', { data, error });

      if (error) {
        console.error('🗑️ Permanent delete RPC Error:', error);
        return { success: false, error: error.message };
      }

      // Check if the function returned false (user doesn't own QR)
      if (data === false) {
        console.warn('🗑️ Permanent delete function returned false - ownership check failed');
        return { success: false, error: 'QR code not found or you do not own it' };
      }

      return { success: data, error: null };
    } catch (error) {
      console.error('🗑️ Permanent delete QR code error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get deleted QR codes (trash/recycle bin)
   */
  static async getDeletedQRCodes(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('qr_codes')
        ?.select(`
          id,
          name,
          qr_type,
          content,
          short_url,
          is_active,
          created_at,
          updated_at,
          deleted_at,
          deleted_by,
          campaigns (
            id,
            name
          )
        `)
        ?.eq('owner_id', userId)
        ?.not('deleted_at', 'is', null)
        ?.order('deleted_at', { ascending: false })
        ?.limit(limit);

      if (error) {
        return { data: [], error: error.message };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Get deleted QR codes error:', error);
      return { data: [], error: error.message };
    }
  }

  /**
   * Bulk soft delete multiple QR codes
   */
  static async bulkSoftDeleteQRCodes(qrCodeIds, userId) {
    try {
      const results = await Promise.allSettled(
        qrCodeIds.map(id => this.softDeleteQRCode(id, userId))
      );

      const successful = results
        .filter(result => result.status === 'fulfilled' && result.value.success)
        .length;

      const failed = results.length - successful;

      return {
        success: true,
        successful,
        failed,
        error: failed > 0 ? `${failed} QR codes failed to delete` : null
      };
    } catch (error) {
      console.error('Bulk soft delete error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Bulk restore multiple QR codes
   */
  static async bulkRestoreQRCodes(qrCodeIds, userId) {
    try {
      const results = await Promise.allSettled(
        qrCodeIds.map(id => this.restoreQRCode(id, userId))
      );

      const successful = results
        .filter(result => result.status === 'fulfilled' && result.value.success)
        .length;

      const failed = results.length - successful;

      return {
        success: true,
        successful,
        failed,
        error: failed > 0 ? `${failed} QR codes failed to restore` : null
      };
    } catch (error) {
      console.error('Bulk restore error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Bulk permanently delete multiple QR codes
   */
  static async bulkPermanentlyDeleteQRCodes(qrCodeIds, userId) {
    try {
      const results = await Promise.allSettled(
        qrCodeIds.map(id => this.permanentlyDeleteQRCode(id, userId))
      );
      console.log(results);

      const successful = results
        .filter(result => result.status === 'fulfilled' && result.value.success)
        .length;

      const failed = results.length - successful;

      return {
        success: true,
        successful,
        failed,
        error: failed > 0 ? `${failed} QR codes failed to permanently delete` : null
      };
    } catch (error) {
      console.error('Bulk permanent delete error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Empty trash (permanently delete all soft-deleted QR codes)
   */
  static async emptyTrash(userId) {
    try {
      // Get all deleted QR codes for this user
      const { data: deletedQRCodes, error: fetchError } = await supabase
        ?.from('qr_codes')
        ?.select('id')
        ?.eq('owner_id', userId)
        ?.not('deleted_at', 'is', null);

      if (fetchError) {
        return { success: false, error: fetchError.message };
      }

      if (!deletedQRCodes || deletedQRCodes.length === 0) {
        return { success: true, deleted: 0, error: null };
      }

      // Permanently delete all
      const result = await this.bulkPermanentlyDeleteQRCodes(
        deletedQRCodes.map(qr => qr.id),
        userId
      );

      return {
        ...result,
        deleted: result.successful
      };
    } catch (error) {
      console.error('Empty trash error:', error);
      return { success: false, error: error.message };
    }
  }
}
